'use client';

import { FC, useMemo } from 'react';
import { Download, Trash2, FileText } from 'lucide-react';
import { DataTable, ColumnDefinition, CellText, Pill } from '@/components/shared/DataTable';
import { ITrainData } from '@/types';
import { formatDateTime } from '@/lib/formatters';

interface ITableProps {
  rows: ITrainData[];
  removeFile: (data: ITrainData) => void;
  isLoading?: boolean;
}

const Table: FC<ITableProps> = ({ rows, removeFile, isLoading = false }: ITableProps) => {
  const downloadFile = (uri: string, fileName: string) => {
    const link = document.createElement('a');
    link.download = fileName;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = useMemo<ColumnDefinition<ITrainData>[]>(
    () => [
      {
        id: 'file',
        label: 'File',
        accessor: 'original_file_name',
        type: 'custom',
        sortable: true,
        renderCell: (value, row) => (
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-slate-400 flex-shrink-0" />
            <CellText variant="primary">{value}</CellText>
          </div>
        ),
      },
      {
        id: 'added_on',
        label: 'Added On',
        accessor: 'upload_timestamp',
        type: 'custom',
        width: '20%',
        sortable: true,
        renderCell: (value) => <CellText variant="secondary">{formatDateTime(value)}</CellText>,
      },
      {
        id: 'status',
        label: 'Training Status',
        accessor: 'processed',
        type: 'custom',
        width: '15%',
        sortable: true,
        renderCell: (value, row) => {
          const failed = Boolean(row.error && row.error !== '__processing__');
          const label = value ? 'Trained' : failed ? 'Failed' : 'Processing';
          const variant = value ? 'success' : failed ? 'error' : 'warning';
          return (
            <Pill
              value={label}
              variant={variant}
              showIcon={false}
              className="min-w-[80px] text-center justify-center"
            />
          );
        },
      },
      {
        id: 'actions',
        label: '',
        accessor: 'file_id',
        type: 'actions',
        width: '15%',
        align: 'right',
        meta: {
          actions: [
            {
              id: 'download',
              label: 'Download',
              icon: Download,
              onClick: (row: ITrainData) => downloadFile(row.blob_url, row.original_file_name),
            },
            {
              id: 'delete',
              label: 'Remove',
              icon: Trash2,
              kind: 'destructive' as const,
              confirm: {
                title: 'Remove this file?',
                message:
                  'This file will be removed from the bot’s training data. The bot will stop using its content. This cannot be undone.',
                confirmLabel: 'Remove',
              },
              onClick: (row: ITrainData) => removeFile(row),
            },
          ],
        },
      },
    ],
    [removeFile],
  );

  return (
    <DataTable<ITrainData>
      data={rows}
      columns={columns}
      rowKey={(row) => row.file_id}
      loading={{
        isLoading,
        skeletonCount: 5,
        message: 'Loading files...',
      }}
      empty={{
        icon: FileText,
        title: 'No files found',
        description: 'Upload a file to get started',
      }}
      ariaLabel="Training files"
    />
  );
};

export default Table;
